import { z } from "zod";

/**
 * Runtime feature flags (PRTL-1353).
 *
 * Stored in the `SiteMeta` table under key "feature-flags" and read through
 * `FeatureFlagService` (@compass/services). Every flag MUST default to
 * `false` so a missing row — or a brand-new flag — is dark until explicitly
 * enabled from the admin Feature Flags page.
 *
 * Flags are temporary: once a feature is enabled globally, remove its gate
 * points and delete the key here.
 */
export const FeatureFlags = z.object({
    /** Compass Calls / meetings hub (PRTL-1299; removal tracked by PRTL-1354). */
    compassCalls: z.boolean().default(false),
    /** Imported iCal calendars: subscriptions polled into the private calendar (PRTL-855–859). */
    calendarImports: z.boolean().default(false),
    calendarReminders: z.boolean().default(false),
    /** Personal reminder events: renewals, birthdays, reviews… with yearly repeat and lead times (PRTL-705). */
    calendarReminderTypes: z.boolean().default(false),
    /** WebP size variants for uploaded public images and admin assets (PRTL-1349). */
    imageVariants: z.boolean().default(false),
    /** In-app registration (RSVP) for COMPASS events, with confirmation emails (PRTL-332). */
    eventRegistration: z.boolean().default(false),
    /** Voice input in navigator Ask Navigator: composer mic + full-screen voice mode (PRTL-713). */
    askNavigatorVoice: z.boolean().default(false),
    /** Navigator OTA web-bundle updates: offer this deploy's signed bundle to matching binaries (PRTL-1370). */
    otaUpdates: z.boolean().default(false),
    /** Navigator dark mode: the System/Light/Dark theme (default System) (PRTL-1462). */
    darkMode: z.boolean().default(false),
});

export type FeatureFlags = z.infer<typeof FeatureFlags>;

export type FeatureFlagName = keyof FeatureFlags;

/** The parsed defaults — what clients assume while loading (fail closed). */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = FeatureFlags.parse({});

/** Human-readable metadata for the admin Feature Flags page. */
export const FEATURE_FLAG_INFO: Record<FeatureFlagName, { label: string; description: string }> = {
    compassCalls: {
        label: "Compass Calls",
        description:
            "The /meetings hub, private-event Zoom join flow, My Calls cards, and the meetings section of the private daily digest.",
    },
    calendarImports: {
        label: "Calendar Imports",
        description:
            "Users' subscribed iCal calendars are polled every 15 minutes and mirrored into their private calendar. Off: the import-calendars task does nothing and no feed is fetched; stored subscriptions and events are kept.",
    },
    calendarReminders: {
        label: "Calendar Reminders",
        description:
            "Push/email reminders one hour before calendar events, a morning push summary of the day, and imported calendars in the private daily digest email. Off: no calendar reminder or summary is sent and the digest email is unchanged.",
    },
    calendarReminderTypes: {
        label: "Calendar Reminder Types",
        description:
            "Personal calendar entries get a type (renewal, expiration, birthday, work anniversary, performance review), all-day dates, a yearly repeat and an optional reminder days or weeks ahead. Off: the type, all-day, repeat and reminder fields can't be set or edited and no lead-time reminders are sent. Entries already saved keep showing as saved — type, all-day dates and yearly repeats — on the calendars, the morning summary and the hour-before reminders.",
    },
    imageVariants: {
        label: "Image Variants",
        description:
            "The worker converts new public image uploads and admin assets into WebP copies at 320/800/1600 px, and feeds/cards serve the smallest one that fits. Off: nothing is converted and every image is served as uploaded; variants already made keep being served.",
    },
    eventRegistration: {
        label: "Event Registration",
        description:
            "Users can register for Compass-hosted events in the app: a Register button on the event, a confirmation email with a calendar invite (.ics), cancellation, an optional reminder an hour before (with Calendar Reminders on), and a registrants list in admin. Off: Compass events show no Register button and no registration email or invite is sent at all — not even when an admin edits or deletes an event — though stored registrations are kept and still cancelled when their event is deleted.",
    },
    askNavigatorVoice: {
        label: "Ask Navigator Voice",
        description:
            "Mobile only. A microphone button in the Ask Navigator composer and the full-screen voice mode: speech is transcribed by Apple's speech recognition into the question box, which the user reviews and sends. Off: no microphone button and the voice screen shows as unavailable; typed Ask Navigator is unchanged.",
    },
    darkMode: {
        label: "Navigator Dark Mode",
        description:
            "Mobile only. The app follows the phone's light/dark setting by default, and Settings offers System / Light / Dark. Off: the app stays light whatever the phone or the stored preference says. Phones remember the last value they saw, so the theme is right at launch before the flag loads.",
    },
    otaUpdates: {
        label: "Navigator OTA Updates",
        description:
            "Mobile only. The app downloads this deploy's signed web bundle and switches to it at its next cold start (or on returning after 5+ minutes away), so JS-only fixes reach phones without an App Store release. Only binaries built with the same native plugins get it. Off: no bundle is offered and phones keep running what they have — turning it off does not roll phones back; redeploy the previous image for that.",
    },
};
